import { HttpClient } from '@angular/common/http';
import { BdAppEnvironmentService } from '@bindoc-projects/platform-lib/data';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AysVirtualClinicAnalysisApi } from '../store/virtual-clinic-analysis.store';
import {
  AysOperatingNumberCatalogItem,
  VirtualClinicAnalysisOperatingNumberPreCalculationResponse,
  VirtualClinicAnalysisOperatingNumberRequest,
  VirtualClinicAnalysisOperatingNumberResponse,
} from '@bindoc-projects/virtual-clinic-analysis-domain-lib';
import {
  CacheService,
  ResourceConfigControlledService,
  ResourceConfigItem,
} from '@bindoc-projects/resource-lib/data';

export class AysVirtualClinicAnalysisService extends ResourceConfigControlledService {
  private _api: AysVirtualClinicAnalysisApi;

  constructor(
    resourceConfig: ResourceConfigItem,
    environmentService: BdAppEnvironmentService,
    cache: CacheService,
    private readonly _httpClient: HttpClient,
  ) {
    super(resourceConfig, environmentService, cache);
    this._api = new AysVirtualClinicAnalysisApi(this.getBaseUrl());
  }

  /**
   * The virtuelle-Klinik Kennzahl catalog.
   *
   * Since BDA-6247 an entry carries more than `id` + `displayName`: a `unit`,
   * the `section` it belongs under and the `parentId` it rolls up into. Every
   * one of those is optional on {@link AysOperatingNumberCatalogItem}, so a
   * caller that only reads the name is unaffected and a backend that predates
   * the fields still type-checks.
   */
  public availableVirtualClinicOperatingNumbers(): Observable<
    AysOperatingNumberCatalogItem[]
  > {
    const url = this._api.availableVirtualClinicOperatingNumbers();

    return this._cache.cacheObservable(
      'availableVirtualClinicOperatingNumbers',
      this._httpClient.get<AysOperatingNumberCatalogItem[]>(url),
    );
  }

  /** The vollstationäre-Fälle catalog — same shape, see the sibling above. */
  public availableFullyInpatientCaseOperatingNumbers(): Observable<
    AysOperatingNumberCatalogItem[]
  > {
    const url = this._api.availableFullyInpatientCaseOperatingNumbers();

    return this._cache.cacheObservable(
      url,
      this._httpClient.get<AysOperatingNumberCatalogItem[]>(url),
    );
  }

  public resolve(
    requests: VirtualClinicAnalysisOperatingNumberRequest,
    additionalCaseFilter?: unknown,
  ): Observable<VirtualClinicAnalysisOperatingNumberResponse> {
    const url = this._api.groupedOperatingNumbers();
    const requestIds = Object.keys(requests).slice();
    const body: {
      requests: VirtualClinicAnalysisOperatingNumberRequest;
      additionalCaseFilter?: unknown;
    } = {
      requests,
    };

    if (additionalCaseFilter) {
      body.additionalCaseFilter = additionalCaseFilter;
    }

    return this._httpClient
      .post<VirtualClinicAnalysisOperatingNumberResponse>(url, body)
      .pipe(
        catchError((error) => {
          let errorResponse = this._createErrorResponse(
            requestIds,
            'Server Fehler',
          );

          if (error.status === 403) {
            errorResponse = this._createErrorResponse(
              requestIds,
              'Keine Berechtigung',
            );
          }

          if (error.status === 400) {
            errorResponse = this._createErrorResponse(
              requestIds,
              'Fehlerhafte Anfrage',
            );
          }

          return of(errorResponse);
        }),
      );
  }

  public preCalculation(
    calculationId: string,
  ): Observable<VirtualClinicAnalysisOperatingNumberPreCalculationResponse> {
    const url = `${this._api.preCalculations()}/${calculationId}/`;
    return this._httpClient.get<VirtualClinicAnalysisOperatingNumberPreCalculationResponse>(
      url,
    );
  }

  private _createErrorResponse(
    requestIds: string[],
    errorMessage: string,
  ): VirtualClinicAnalysisOperatingNumberResponse {
    const response: VirtualClinicAnalysisOperatingNumberResponse = {
      requests: {},
    };

    requestIds.forEach((requestId) => {
      response.requests[requestId] = {
        statusType: 'error',
        aggregatedValue: null,
        additionalInformation: null,
        groupedValues: null,
        errors: [{ text: { de: errorMessage } }],
      };
    });

    return response;
  }
}
